import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface MatrixFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: Record<string, string>) => void;
}

export function MatrixField({ palette, question, value, onChange }: MatrixFieldProps): JSX.Element {
  const rows = question.settings?.rows ?? [];
  const columns = question.settings?.columns ?? [];
  const current = (value as Record<string, string>) ?? {};

  const handleSelect = (row: string, column: string): void => {
    onChange({ ...current, [row]: column });
  };

  return (
    <div style={wrapperStyle()}>
      <table style={tableStyle()}>
        <thead>
          <tr>
            <th style={rowHeaderStyle(palette)} />
            {columns.map((column) => (
              <th key={column} style={columnHeaderStyle(palette)}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <th scope="row" style={rowLabelStyle(palette)}>
                {row}
              </th>
              {columns.map((column) => {
                const selected = current[row] === column;
                return (
                  <td key={column} style={cellStyle(palette)}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => handleSelect(row, column)}
                      className="chirp-btn"
                      aria-label={`${row}: ${column}`}
                      style={radioButtonStyle(palette, selected)}
                    >
                      {selected && <span style={radioDotStyle(palette)} />}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function wrapperStyle(): CSSProperties {
  // Avoid overflowX:auto so the row labels never get clipped off-screen on
  // narrow viewports. The table itself is constrained to 100% width and the
  // row label column wraps text instead of forcing a horizontal scroll.
  return { width: '100%' };
}

function tableStyle(): CSSProperties {
  return { width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' };
}

function rowHeaderStyle(palette: Palette): CSSProperties {
  // Reserve ~40% of table width for row labels on mobile; the remaining
  // columns share the rest and use their natural minimum widths.
  return { borderBottom: `1px solid ${palette.border}`, padding: '8px 8px', width: '40%' };
}

function columnHeaderStyle(palette: Palette): CSSProperties {
  return {
    padding: '8px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 500,
    color: palette.textMuted,
    borderBottom: `1px solid ${palette.border}`,
    wordBreak: 'break-word',
  };
}

function rowLabelStyle(palette: Palette): CSSProperties {
  return {
    padding: '10px 8px 10px 0',
    textAlign: 'left',
    fontSize: 13,
    color: palette.text,
    fontWeight: 500,
    borderBottom: `1px solid ${hexWithAlpha(palette.textMuted, 0.1)}`,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: 1.3,
  };
}

function cellStyle(palette: Palette): CSSProperties {
  return {
    textAlign: 'center',
    padding: '10px 2px',
    borderBottom: `1px solid ${hexWithAlpha(palette.textMuted, 0.1)}`,
  };
}

function radioButtonStyle(palette: Palette, selected: boolean): CSSProperties {
  // Generous 32px hit area on mobile (button itself is 24×24 visually);
  // padding ensures even narrow rows are tappable.
  return {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: `2px solid ${selected ? palette.primary : palette.border}`,
    background: selected ? hexWithAlpha(palette.primary, 0.08) : 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    transition: 'border-color 120ms ease, background 120ms ease',
  };
}

function radioDotStyle(palette: Palette): CSSProperties {
  return { width: 10, height: 10, borderRadius: '50%', background: palette.primary };
}
